import { useMediaQuery } from '@mui/material';

/**
 * Centralized breakpoint values (must match _breakpoints.scss)
 * These values represent min-width thresholds
 */
export const breakpoints = {
  xs: 414,      // Small mobile
  sm: 480,      // Mobile
  md: 640,      // Large mobile / Small tablet
  tablet: 768,  // Tablet
  lg: 1024,     // Desktop (sidebar breakpoint)
  xl: 1200,    // Large desktop
  xxl: 1366,   // Wide desktop
  hd: 1920,    // Full HD
} as const;

export type BreakpointKey = keyof typeof breakpoints;

/**
 * Hook to check if viewport is at or above a breakpoint (min-width)
 * @param breakpoint - The breakpoint key to check against
 * @returns boolean - true if viewport width >= breakpoint value
 *
 * @example
 * const isDesktop = useBreakpointUp('lg');  // true if >= 1024px
 * const isTablet = useBreakpointUp('tablet');  // true if >= 768px
 */
export function useBreakpointUp(breakpoint: BreakpointKey): boolean {
  return useMediaQuery(`(min-width: ${breakpoints[breakpoint]}px)`);
}

/**
 * Hook to check if viewport is below a breakpoint (max-width)
 * @param breakpoint - The breakpoint key to check against
 * @returns boolean - true if viewport width < breakpoint value
 *
 * @example
 * const isMobile = useBreakpointDown('lg');  // true if < 1024px
 * const isSmallScreen = useBreakpointDown('tablet');  // true if < 768px
 */
export function useBreakpointDown(breakpoint: BreakpointKey): boolean {
  return useMediaQuery(`(max-width: ${breakpoints[breakpoint] - 1}px)`);
}

/**
 * Hook to check if viewport is between two breakpoints
 * @param min - The minimum breakpoint (inclusive)
 * @param max - The maximum breakpoint (exclusive)
 * @returns boolean - true if min <= viewport width < max
 *
 * @example
 * const isTabletOnly = useBreakpointBetween('tablet', 'lg');  // 768px - 1023px
 */
export function useBreakpointBetween(min: BreakpointKey, max: BreakpointKey): boolean {
  return useMediaQuery(
    `(min-width: ${breakpoints[min]}px) and (max-width: ${breakpoints[max] - 1}px)`
  );
}

/** Viewports at most this tall (landscape phones) can't fit anchored popovers. */
export const SHORT_VIEWPORT_MAX_HEIGHT = 500;

/**
 * Hook to check if the viewport is short (max-height), e.g. a phone in
 * landscape — overlays should then prefer full-width sheets over popovers.
 */
export function useShortViewport(): boolean {
  return useMediaQuery(`(max-height: ${SHORT_VIEWPORT_MAX_HEIGHT}px)`);
}

/**
 * Hook returning current breakpoint info
 * @returns Object with boolean flags for each breakpoint
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useBreakpoint();
 */
export function useBreakpoint() {
  const isXs = useBreakpointUp('xs');
  const isSm = useBreakpointUp('sm');
  const isMd = useBreakpointUp('md');
  const isTablet = useBreakpointUp('tablet');
  const isLg = useBreakpointUp('lg');
  const isXl = useBreakpointUp('xl');
  const isXxl = useBreakpointUp('xxl');
  const isHd = useBreakpointUp('hd');

  return {
    // Min-width flags (is at or above)
    isXs,
    isSm,
    isMd,
    isTablet,
    isLg,
    isXl,
    isXxl,
    isHd,
    // Semantic aliases
    isMobile: !isLg,        // Below 1024px
    isDesktop: isLg,        // 1024px and above
    isLargeDesktop: isXl,   // 1200px and above
  };
}

export default useBreakpoint;
